// Pure predicate for "is this a 2-record household merge cluster?"
// Split out of cleanup-records.ts so it can be unit-tested from
// tests/web/ without dragging web/src/api.ts (DOM-typed: fetch with
// {credentials}, import.meta.env) into the root tsconfig program.
//
// Same convention as deep-link.ts: a local structural `IssueLike`
// type covers the fields this predicate reads; cleanup-records.ts
// is a thin wrapper that passes a real CleanupIssueSummary through
// unchanged (CleanupIssueSummary is a structural superset of
// IssueLike, so the wrapper is a one-liner).

export type RecordRef = { kind: string; id: string; label: string };

// Minimal subset of CleanupIssueSummary this predicate inspects.
export interface IssueLike {
  entityKind: string;
  affectedRecords?: RecordRef[];
  recommendation?: { verb: string } | null;
  detectorSlug?: string;
}

// 2-record same-kind household cluster recommended for merge → return
// the two refs; otherwise null. Restricted to merge-recommended issues
// so we don't surface the survivor-pick / per-field review button on
// unrelated household issues. N≥3 is intentionally null today: the
// HouseholdClusterMergeOverlay handles only the 2-record shape (stub
// + candidate from the mortgage stub-household-merge-candidate
// detector), and surfacing it for 3+ would suggest a flow that
// isn't built yet.
//
// Grant 2026-07-25 (Donna's Needham/Needam report) —
// household-address-split-family's "different surname" branch
// deliberately emits NO recommendation at all: same address + different
// surnames is often two unrelated families (a house sold between
// clients), so the detector refuses to auto-suggest merge (Grant's
// "auto-merge nothing" call). But that left a genuine dead end — the
// issue's own flagExplanation says "if it is one family, merge", and
// there was no merge affordance anywhere in the drawer to act on that.
// Slug-gated (not a blanket "any no-recommendation household issue can
// merge") so unrelated no-recommendation household issues — e.g.
// hh-no-primary, spouse-pass — don't sprout a misleading merge button;
// only this specific "confirm it's one family, then merge" case does.
export function isUnconfirmedSplitFamily(issue: IssueLike): boolean {
  return (
    !issue.recommendation &&
    issue.detectorSlug === "household-address-split-family"
  );
}

// Grant 2026-09-15 — the import-stub review cards that NAME a candidate
// family had no merge affordance at all, which is the worst possible
// place for the gap to be.
//
// Measured on production the day this was written: of 41 open
// mortgage-import-stub-review cards, 27 carry TWO household refs (the
// placeholder, and the family its loan points at) and no recommendation.
// householdSearchMergeSource only fires on ONE household ref, and
// householdClusterRefs only fired on verb === "merge". Both said no, so
// those 27 fell through to the generic disposition buttons — not an
// issue, snooze, assign — with nowhere to record the answer even once
// someone had worked it out. The 14 cards that name NO candidate were
// the only ones with a working button, which is exactly backwards.
//
// Same reasoning as isUnconfirmedSplitFamily above, and the same shape:
// no recommendation because the data does not settle it, two households
// because the research is done, and a person who can see both. It opens
// the per-field review, never a blind one-click merge.
//
// Slug-gated on purpose. "Two household refs and no recommendation" as a
// blanket rule would sprout a merge button on unrelated household issues
// that emit two refs for context rather than for merging.
const STUB_REVIEW_WITH_A_CANDIDATE = new Set([
  "mortgage-import-stub-review",
  "financial-import-stub-review",
]);

export function isImportStubWithCandidate(issue: IssueLike): boolean {
  if (issue.recommendation) return false;
  const slug = issue.detectorSlug;
  return slug != null && STUB_REVIEW_WITH_A_CANDIDATE.has(slug);
}

// ── "Move the loan" is only one of the answers (Grant, 2026-09-16) ────
//
// mortgage-loan-wrong-household asks "this loan is filed under X
// Household but its borrower lives in Y Household, should it move?" and,
// since 2026-09-15, offers a working move button for it.
//
// Reading the 83 open cards on production by name, the move is the WRONG
// answer on a large share of them, because X and Y are the same person
// written down twice: Jon Dekkers and Jonathan Dekkers. Nicholaus Smith
// and Nick Smith. Mat Barkley and Mathew Barkley. Brad Allen and Bradley
// Allen. Gary J Streett and Gary Streett. Karl Trae Sadler and Karl
// Sadler. William Van Camp and William Camp. Moving a loan between two
// households that are one household fixes nothing; it files the record
// under the duplicate and leaves the duplicate standing. That is exactly
// the shape behind the twelve loan moves reversed on 2026-09-15.
//
// The detector DOES have a merge route, but it is gated on the two
// households' street addresses agreeing, and measured on production that
// gate holds for 0 of the 67 cards carrying two households — the
// duplicate was created from a different source, with a different
// address, which is why it is a duplicate.
//
// So the card offers the merge as a SECOND answer and lets the person
// choose. This deliberately does not teach the OS to decide which it is:
// the one thing tonight proved is that guessing between "same person" and
// "two people" on a surname is what does the damage. Both answers open a
// reviewed, reversible flow.
//
// Gated on the declared fix being a record MOVE, so the sibling cards
// from the same detector — the "one loan recorded twice" keep-one shape,
// which carries the same two household refs — never grow a merge button
// for a question they are not asking.
const LOAN_PLACEMENT_SLUG = "mortgage-loan-wrong-household";

export interface LoanMergeAlternative {
  /** The household the loan sits on, and the merge survivor: it already
   *  holds the record, and on these pairs the other side is usually the
   *  thinner copy. One click swaps it in the review either way. */
  source: RecordRef;
  /** The household the borrower was found in. */
  destination: RecordRef;
}

export function loanHouseholdMergeAlternative(
  issue: IssueLike & { fix?: { kind?: string } | null },
): LoanMergeAlternative | null {
  if (issue.detectorSlug !== LOAN_PLACEMENT_SLUG) return null;
  // Only the placement question. A keep-one duplicate card is asking
  // which ROW is real, which a household merge would not answer.
  if (issue.fix?.kind !== "move-record") return null;
  const households = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  if (households.length !== 2) return null;
  const [source, destination] = households as [RecordRef, RecordRef];
  if (source.id === destination.id) return null;
  return { source, destination };
}

export function householdClusterRefs(
  issue: IssueLike,
): RecordRef[] | null {
  if (issue.entityKind !== "household") return null;
  const isRecommendedMerge = issue.recommendation?.verb === "merge";
  if (
    !isRecommendedMerge &&
    !isUnconfirmedSplitFamily(issue) &&
    !isImportStubWithCandidate(issue)
  ) {
    return null;
  }
  const recs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  return recs.length === 2 ? recs : null;
}
